<ng-container *ngIf="viewport.isMobile(); else desktopShell">
  <ion-app>
    <ion-router-outlet></ion-router-outlet>
    <app-mobile-file-preview-modal />
    <app-mobile-slow-connection-hint />
  </ion-app>
</ng-container>

<ng-template #desktopShell>
<!-- Global HTTP and Route Loading Indicator -->
<div class="global-loading-bar" *ngIf="loadingIndicator.isLoading()">
  <nz-progress
    [nzPercent]="loadingIndicator.progress()"
    [nzShowInfo]="false"
    nzStatus="active"
    [nzStrokeColor]="'var(--intent-info)'"
  ></nz-progress>
</div>

<nz-layout class="app-layout" data-layout="desktop">
  <nz-header *ngIf="!hideAppChrome() && !hideHeader()">
    <div class="header-content">
      <button
        *ngIf="(isLoggedIn$ | async) === true"
        type="button"
        class="menu-toggle"
        [class.menu-toggle--open]="mobileMenuOpen"
        data-testid="mobile-menu-toggle"
        (click)="toggleMobileMenu()"
        [attr.aria-expanded]="mobileMenuOpen"
        aria-controls="primary-navigation"
        [attr.aria-label]="'app.a11y.mainMenu' | translate"
      >
        <span class="menu-toggle__bars" aria-hidden="true">
          <span class="menu-toggle__bar"></span>
          <span class="menu-toggle__bar"></span>
          <span class="menu-toggle__bar"></span>
        </span>
        <span class="sr-only">{{ "app.a11y.menu" | translate }}</span>
      </button>
      <a
        class="brand"
        [routerLink]="(isLoggedIn$ | async) ? resolveDefaultLandingRoute((roleValue$ | async) ?? 0) : '/'"
        [attr.aria-label]="'app.a11y.homeLink' | translate"
      >
        <img
          class="brand-mark"
          src="electa_logo.svg"
          width="46"
          height="46"
          alt=""
          aria-hidden="true"
          loading="eager"
        />
        <span>{{ "app.brand.title" | translate }}</span>
      </a>
      <nav
        id="primary-navigation"
        class="primary-nav"
        [class.open]="mobileMenuOpen"
        [attr.aria-label]="'app.a11y.primaryNavigation' | translate"
      >
        <!-- Desktop / tablet: adaptive Priority+ nav with overflow dropdown -->
        <app-overflow-nav
          *ngIf="(isLoggedIn$ | async) === true"
          class="main-nav-host"
          [items]="primaryNavItems()"
          [dotKeys]="navDotKeys()"
          [ariaLabel]="'app.a11y.primaryNavigation' | translate"
          (itemClick)="onPrimaryNavClick()"
        ></app-overflow-nav>

        <!-- Mobile drawer: plain vertical list so everything is directly reachable -->
        <ul nz-menu nzMode="vertical" class="main-nav main-nav--drawer">
          <li
            *ngFor="let item of primaryNavItems()"
            nz-menu-item
            routerLinkActive="ant-menu-item-selected"
          >
            <a [routerLink]="item.route" (click)="onPrimaryNavClick()">
              <nz-icon [nzType]="item.icon" />
              {{ item.labelKey | translate }}
              <span
                *ngIf="item.key === 'diskussionen' && unreadDiscussionCount() > 0"
                class="main-nav-drawer-dot"
                aria-hidden="true"
              ></span>
            </a>
          </li>
        </ul>
        <div
          class="nav-actions"
          [attr.aria-label]="'app.a11y.userActions' | translate"
        >
          <ng-container *ngIf="(isLoggedIn$ | async) === true">
            <div class="utility-quick-links">
              <button
                type="button"
                nz-button
                nzType="text"
                nzSize="small"
                class="utility-quick-link  desktop-button"
                *ngFor="let section of utilitySections"
                (click)="openSection(section)"
                [class.active]="isUtilitySectionActive(section)"
                [attr.aria-label]="sectionLabel(section)"
                [attr.data-section]="section"
              >
                <nz-badge
                  nzSize="small"
                  [nzCount]="sectionCount(section)"
                  [nzOverflowCount]="99"
                  [class.utility-badge--notifications]="section === UTILITY_DOCK.TAB.NOTIFICATIONS"
                  [nzColor]="
                    sectionCount(section) > 0
                      ? (section === UTILITY_DOCK.TAB.INBOX
                          ? 'var(--intent-info)'
                          : section === UTILITY_DOCK.TAB.NOTIFICATIONS
                            ? 'var(--badge-notification-bg)'
                            : undefined)
                      : undefined
                  "
                >
                  <nz-icon
                    nzType="{{ sectionIcon(section) }}"
                    class="utility-icon"
                  ></nz-icon>
                </nz-badge>
                <span>{{ sectionLabel(section) }}</span>
              </button>

              <button
                type="button"
                nz-button
                nzType="text"
                nzSize="small"
                class="utility-quick-link  desktop-button"
                (click)="openPrivateTasks()"
                [class.active]="isPrivateTasksActive()"
                [attr.aria-label]="'app.private_tasks_3' | translate"
                data-section="private-tasks"
              >
                <nz-badge
                  nzSize="small"
                  [nzCount]="privateTasksCount()"
                  [nzOverflowCount]="99"
                  [nzColor]="privateTasksCount() > 0 ? 'var(--intent-info-muted)' : undefined"
                >
                  <nz-icon nzType="lock" class="utility-icon"></nz-icon>
                </nz-badge>
                <span>{{ 'app.private_tasks' | translate }}</span>
              </button>
            </div>
            <!--<button
              type="button"
              class="action-pill action-pill--ghost utility-toggle"
              (click)="utilityDock.toggle()"
              [attr.aria-expanded]="utilityDock.isOpen()"
              [attr.aria-label]="utilityDockButtonAriaLabel()"
            >
              <nz-icon nzType="menu"></nz-icon>
            </button>-->
          </ng-container>
          <ng-container *ngIf="!hideHeaderAuth()">
            <ng-container
              *ngIf="(isLoggedIn$ | async) === false; else userActions"
            >
              <a
                class="action-pill action-pill--primary"
                routerLink="/"
                (click)="closeMobileMenu()"
              >
                <nz-icon nzType="login" />
                <span>{{ "app.actions.login" | translate }}</span>
              </a>
            </ng-container>
            <ng-template #userActions>
              <div class="user-actions">
                <button
                  type="button"
                  class="action-pill action-pill--avatar"
                  nz-dropdown
                  [nzDropdownMenu]="userMenu"
                  nzTrigger="click"
                  nzPlacement="bottomRight"
                  (nzVisibleChange)="userMenuOpen = $event"
                  aria-haspopup="menu"
                  [attr.aria-expanded]="userMenuOpen"
                  [attr.aria-label]="'app.a11y.userMenu' | translate"
                  [attr.title]="'app.actions.account' | translate"
                >
                  <ng-container *ngIf="member$ | async as member; else fallbackToAuthUser">
                    <app-member-avatar
                      [member]="member"
                      size="md"
                      [onImageError]="onAvatarError.bind(this)"
                    ></app-member-avatar>
                  </ng-container>
                  <ng-template #fallbackToAuthUser>
                    <ng-container *ngIf="user$ | async as user; else avatarPlaceholder">
                      <app-member-avatar
                        [identity]="{ firstName: getFirstName(user.name), lastName: getLastName(user.name), email: user.email }"
                        size="md"
                        [onImageError]="onAvatarError.bind(this)"
                      ></app-member-avatar>
                    </ng-container>
                  </ng-template>
                  <ng-template #avatarPlaceholder>
                    <app-member-avatar></app-member-avatar>
                  </ng-template>
                  <span class="action-pill__label">{{ "app.actions.account" | translate }}</span>
                </button>
                <nz-dropdown-menu #userMenu="nzDropdownMenu">
                  <ul nz-menu>
                    <li
                      nz-menu-item
                      routerLink="/manage-profile"
                      (click)="closeMobileMenu()"
                    >
                      <nz-icon nzType="setting" />
                      <span>{{ "app.actions.account" | translate }}</span>
                    </li>
                    <li nz-menu-item (click)="openPrivateTasks()">
                      <nz-icon nzType="lock" />
                      <span>{{ 'app.private_tasks_1' | translate }}</span>
                    </li>
                    <li nz-menu-item (click)="closeMobileMenu(); logout()">
                      <nz-icon nzType="logout" />
                      <span>{{ "app.actions.logout" | translate }}</span>
                    </li>
                  </ul>
                </nz-dropdown-menu>
              </div>
            </ng-template>
          </ng-container>
        </div>
      </nav>
      <!-- Mobile Top-Right Controls: Notifications and Profile Avatar -->
      <div class="mobile-header-center" *ngIf="(isLoggedIn$ | async) === true">
        <button
          type="button"
          class="mobile-header-icon  desktop-button"
          data-testid="mobile-notifications-button"
          nz-button
          nzType="text"
          nzSize="small"
          (click)="openSection(UTILITY_DOCK.TAB.NOTIFICATIONS)"
          [attr.aria-label]="'Notifications' + (sectionCount(UTILITY_DOCK.TAB.NOTIFICATIONS) > 0 ? ', ' + sectionCount(UTILITY_DOCK.TAB.NOTIFICATIONS) : '')"
        >
          <nz-badge
            class="utility-badge--notifications"
            [nzCount]="sectionCount(UTILITY_DOCK.TAB.NOTIFICATIONS)"
            [nzOverflowCount]="99"
            [nzColor]="sectionCount(UTILITY_DOCK.TAB.NOTIFICATIONS) > 0 ? 'var(--badge-notification-bg)' : undefined"
          >
            <nz-icon nzType="bell"></nz-icon>
          </nz-badge>
        </button>
      </div>

      <div class="mobile-header-actions" *ngIf="(isLoggedIn$ | async) === true">
        <!-- Profile Avatar (replaces hamburger menu on mobile) -->
        <button
          type="button"
          class="mobile-profile-avatar"
          nz-dropdown
          [nzDropdownMenu]="mobileUserMenu"
          nzTrigger="click"
          nzPlacement="bottomRight"
          (nzVisibleChange)="userMenuOpen = $event"
          aria-haspopup="menu"
          [attr.aria-expanded]="userMenuOpen"
          [attr.aria-label]="'Profile menu' | translate"
        >
          <ng-container *ngIf="member$ | async as member; else fallbackToAuthUserMobile">
            <app-member-avatar
              [member]="member"
              size="sm"
              [onImageError]="onAvatarError.bind(this)"
            ></app-member-avatar>
          </ng-container>
          <ng-template #fallbackToAuthUserMobile>
            <ng-container *ngIf="user$ | async as user; else avatarPlaceholder">
              <app-member-avatar
                [identity]="{ firstName: getFirstName(user.name), lastName: getLastName(user.name), email: user.email }"
                size="sm"
                [onImageError]="onAvatarError.bind(this)"
              ></app-member-avatar>
            </ng-container>
          </ng-template>
          <ng-template #avatarPlaceholder>
            <app-member-avatar size="sm"></app-member-avatar>
          </ng-template>
        </button>

        <!-- Mobile User Menu Dropdown -->
        <nz-dropdown-menu #mobileUserMenu="nzDropdownMenu">
          <ul nz-menu>
            <li
              nz-menu-item
              routerLink="/manage-profile"
              (click)="closeMobileMenu()"
            >
              <nz-icon nzType="setting" />
                      <span>{{ "app.actions.account" | translate }}</span>
            </li>
            <li nz-menu-item (click)="openPrivateTasks()">
              <nz-icon nzType="lock" />
              <span>{{ 'app.private_tasks_2' | translate }}</span>
            </li>
            <li nz-menu-item (click)="closeMobileMenu(); logout()">
              <nz-icon nzType="logout" />
                      <span>{{ "app.actions.logout" | translate }}</span>
            </li>
          </ul>
        </nz-dropdown-menu>

      </div>
    </div>
    <div
      class="nav-overlay"
      *ngIf="mobileMenuOpen"
      (click)="closeMobileMenu()"
    ></div>
  </nz-header>
  <nz-layout class="app-inner-layout">
    <nz-sider *ngIf="false">{{ "app.layout.leftSidebar" | translate }}</nz-sider>
    <nz-content [class.chromeless]="hideAppChrome()">
      <router-outlet></router-outlet>
    </nz-content>
    <nz-sider *ngIf="false">{{ "app.layout.rightSidebar" | translate }}</nz-sider>
  </nz-layout>
  <nz-footer *ngIf="!hideAppChrome()">
    <div class="footer-content">
      <div class="footer-meta">
        <div class="footer-brand">
          <strong>{{ "app.brand.title" | translate }}</strong>
          —
          {{ "app.footer.tagline" | translate }}
        </div>
        <div class="env-details">
          <span class="env-container"
            >{{ "app.footer.environment" | translate }}:
            {{ env.state }}</span
          >
          <span class="env-container" *ngIf="!env.production"
            >{{ "app.footer.commit" | translate }}: {{ sha }}</span
          >
        </div>
      </div>
      <div class="footer-actions">
        <div
          class="language-switch"
          [attr.aria-label]="'app.languageSwitch.ariaLabel' | translate"
        >
          <a
            href=""
            (click)="$event.preventDefault(); switchLocale('de')"
            [class.active]="activeLocale.startsWith('de')"
            >{{ "app.languageSwitch.de" | translate }}</a
          >
          <span aria-hidden="true">/</span>
          <a
            href=""
            (click)="$event.preventDefault(); switchLocale('en')"
            [class.active]="activeLocale.startsWith('en')"
            >{{ "app.languageSwitch.en" | translate }}</a
          >
          <span aria-hidden="true">/</span>
          <a
            href=""
            (click)="$event.preventDefault(); switchLocale('fr')"
            [class.active]="activeLocale.startsWith('fr')"
            >{{ "app.languageSwitch.fr" | translate }}</a
          >
          <span aria-hidden="true">/</span>
          <a
            href=""
            (click)="$event.preventDefault(); switchLocale('it')"
            [class.active]="activeLocale.startsWith('it')"
            >{{ "app.languageSwitch.it" | translate }}</a
          >
        </div>
      </div>
    </div>
  </nz-footer>
</nz-layout>

<!-- Utility Dock (Drawer for inbox/notifications/todos) -->
<app-utility-dock *ngIf="!hideAppChrome()" />

<!-- Global File Preview Modal -->
<app-file-preview-modal />

<!-- Global Upload Tray (desktop only) -->
<app-file-upload-tray />

<!-- Feedback Button (visible only when logged in) -->
<app-feedback-button *ngIf="isLoggedIn$ | async" />

<!-- Global Maintenance Overlay -->
<app-maintenance-screen />
</ng-template>

